import { DefaultValue, transitionToMiliseconds, useComponentToggle } from '@billy/design-system'
import noop from 'lodash/noop'
import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

import { ORGANIZATIONS_SEARCH_SHORTCUT } from '../constants/shortcuts'
import { useGlobalSearch } from './globalSearchContext'

export type OrganizationsSearchContextState = {
  isOpen: boolean
  isRendered: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}

const initialState: OrganizationsSearchContextState = {
  isOpen: false,
  isRendered: false,
  setIsOpen: noop,
}

export const OrganizationsSearchContext = createContext<OrganizationsSearchContextState>(initialState)

interface OrganizationsSearchContextProviderProps {
  children: ReactNode
}

export const OrganizationsSearchContextProvider = ({
  children,
}: OrganizationsSearchContextProviderProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const [, globalSearchDispatch] = useGlobalSearch()

  const { isVisible, isRendered } = useComponentToggle({
    isOpen,
    animationTime: transitionToMiliseconds(DefaultValue.TransitionDuration),
  })

  const value: OrganizationsSearchContextState = {
    isOpen: isVisible,
    isRendered,
    setIsOpen,
  }

  const handleGlobalShortcutPress = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault()
      // Close the global search popup so the two don't overlap
      globalSearchDispatch({ type: 'CLOSE' })
      setIsOpen(true)
    },
    [globalSearchDispatch, setIsOpen],
  )

  useHotkeys(
    ORGANIZATIONS_SEARCH_SHORTCUT,
    handleGlobalShortcutPress,
    { enableOnTags: ['INPUT', 'TEXTAREA', 'SELECT'] },
    [setIsOpen],
  )

  return <OrganizationsSearchContext.Provider value={value}>{children}</OrganizationsSearchContext.Provider>
}

export const useOrganizationsSearch = () => {
  const context = useContext(OrganizationsSearchContext)

  if (!context) {
    throw new Error('OrganizationsSearchContextProvider is missing in the module!')
  }

  return context
}
